import { ContactClient } from "@amazon-connect/contact";
import type { AmazonConnectProvider } from "@amazon-connect/core";
import type { ArgSpec, ClientModule } from "../types";

const CONTACT_ID: readonly ArgSpec[] = [
  { k: "contactId", type: "contactId", required: true },
];

const PARTICIPANT_ID: readonly ArgSpec[] = [
  { k: "participantId", type: "participantId", required: true },
];

/**
 * ContactClient: the primary source of contact lifecycle events and attribute data.
 * Every argument list below is positional and ordered to match the shipped 1.0.14
 * `contact-client.d.ts` — see `sdk-signatures.md`, not the `getAttribute(contactId, key)`
 * or `getParticipant(contactId, participantId)` shapes the original plan assumed.
 */
export const contactModule: ClientModule = {
  id: "contact",
  label: "Contact",
  pkg: "@amazon-connect/contact",
  clientExport: "ContactClient",
  kind: "observable",
  create: (provider: AmazonConnectProvider) =>
    new ContactClient({ provider }) as unknown as Record<string, unknown>,
  events: [
    { name: "onIncoming", off: "offIncoming" },
    { name: "onConnecting", off: "offConnecting" },
    { name: "onConnected", off: "offConnected" },
    { name: "onPending", off: "offPending" },
    { name: "onMissed", off: "offMissed" },
    { name: "onStartingAcw", off: "offStartingAcw" },
    { name: "onCleared", off: "offCleared" },
    // @deprecated in the SDK: "Use onCleared / offCleared instead".
    { name: "onDestroyed", off: "offDestroyed", deprecated: true },
    { name: "onError", off: "offError" },
    { name: "onParticipantAdded", off: "offParticipantAdded" },
    { name: "onParticipantDisconnected", off: "offParticipantDisconnected" },
    { name: "onParticipantStateChanged", off: "offParticipantStateChanged" },
  ],
  requests: [
    { name: "getContact", args: CONTACT_ID },
    // getAttributes(contactId, attributes: ContactAttributeFilter). ContactAttributeFilter is
    // ContactAttributeKey[] | "*" — a "string" type made the array branch unreachable from the
    // UI, so this is json (still defaulting to "*" when the field is left blank).
    {
      name: "getAttributes",
      args: [...CONTACT_ID, { k: "attributes", type: "json", default: "*" }],
    },
    // getAttribute(contactId, attribute) — the SDK parameter is named "attribute", not "key".
    {
      name: "getAttribute",
      args: [...CONTACT_ID, { k: "attribute", type: "string", required: true }],
    },
    { name: "getInitialContactId", args: CONTACT_ID },
    // @deprecated in the SDK: "Use getChannelType instead".
    { name: "getType", args: CONTACT_ID, deprecated: true },
    { name: "getChannelType", args: CONTACT_ID },
    { name: "getStateDuration", args: CONTACT_ID },
    { name: "getQueue", args: CONTACT_ID },
    { name: "getQueueTimestamp", args: CONTACT_ID },
    { name: "getContactRegion", args: CONTACT_ID },
    // getInstanceDetails(contactId) — the plan had this as a zero-arg call; it is not.
    { name: "getInstanceDetails", args: CONTACT_ID },
    { name: "listContacts", args: [] },
    { name: "listParticipants", args: CONTACT_ID },
    // getParticipant(participantId) — participant-scoped only, no contactId.
    { name: "getParticipant", args: PARTICIPANT_ID },
    { name: "getParticipantState", args: PARTICIPANT_ID },
    { name: "isAutoAcceptEnabled", args: CONTACT_ID },
    { name: "isPreviewMode", args: CONTACT_ID },
    { name: "getPreviewConfiguration", args: CONTACT_ID },
    { name: "accept", args: CONTACT_ID, mutating: true },
    { name: "reject", args: CONTACT_ID, mutating: true },
    { name: "clear", args: CONTACT_ID, mutating: true },
    { name: "disconnectSelf", args: CONTACT_ID, mutating: true },
    // addParticipant(contactId, quickConnect) — the second arg is a QuickConnect, not "endpoint".
    {
      name: "addParticipant",
      args: [
        ...CONTACT_ID,
        { k: "quickConnect", type: "json", required: true },
      ],
      mutating: true,
    },
    // disconnectParticipant(participantId) — participant-scoped only, no contactId.
    { name: "disconnectParticipant", args: PARTICIPANT_ID, mutating: true },
    // transfer(contactId, quickConnect) — the second arg is a QuickConnect, not "endpoint".
    {
      name: "transfer",
      args: [
        ...CONTACT_ID,
        { k: "quickConnect", type: "json", required: true },
      ],
      mutating: true,
    },
    { name: "engagePreviewContact", args: CONTACT_ID, mutating: true },
  ],
};
